import { useState, useCallback, useRef } from 'react';
import styles from './CatalogoFornecedor.module.css';

const API_BASE = import.meta.env.VITE_API_URL || '';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatarMoeda = (valor) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);

const formatarFonte = (fonte) =>
  fonte === 'ecommerce' ? '🛒 E-commerce' : '🏪 Loja Física';

// Token segue o mesmo padrão do api.js (linha 18)
const getToken = () => localStorage.getItem('token');

// ─── Componente de Card de Resultado ────────────────────────────────────────

function CardResultado({ item }) {
  const [imgError, setImgError] = useState({});

  return (
    <div className={`${styles.card} ${item.encontrado ? styles.cardEncontrado : styles.cardNaoEncontrado}`}>
      {/* Badge de status */}
      <div className={styles.cardBadge}>
        {item.encontrado ? (
          <span className={styles.badgeEncontrado}>✅ Encontrado</span>
        ) : (
          <span className={styles.badgeNaoEncontrado}>❌ Não cadastrado</span>
        )}
        <span className={styles.badgePagina}>Pág. {item.catalogo.pagina}</span>
      </div>

      {/* Informação do catálogo */}
      <div className={styles.cardCatalogo}>
        <div className={styles.catalogoIcone}>
          {item.catalogo.categoria === 'anel' ? '💍' :
           item.catalogo.categoria === 'colar' ? '📿' :
           item.catalogo.categoria === 'brinco' ? '✨' :
           item.catalogo.categoria === 'pulseira' ? '⌚' :
           item.catalogo.categoria === 'pingente' ? '🔮' :
           item.catalogo.categoria === 'relogio' ? '🕐' : '📦'}
        </div>
        <div className={styles.catalogoInfo}>
          <h3 className={styles.catalogoNome}>{item.catalogo.nome}</h3>
          {item.catalogo.codigo && (
            <span className={styles.catalogoCodigo}>Ref.: {item.catalogo.codigo}</span>
          )}
          {item.catalogo.categoria && (
            <span className={styles.catalogoCategoria}>{item.catalogo.categoria}</span>
          )}
          {item.catalogo.caracteristicas && (
            <p className={styles.catalogoCaracteristicas}>{item.catalogo.caracteristicas}</p>
          )}
        </div>
      </div>

      {/* Produtos encontrados no estoque */}
      {item.encontrado && item.produtos.length > 0 && (
        <div className={styles.produtosEncontrados}>
          <div className={styles.produtosHeader}>
            <span>Mais parecidos na loja ({item.produtos.length})</span>
          </div>

          {item.produtos.map((prod, idx) => (
            <div key={idx} className={styles.produtoCard}>
              {/* Imagem do produto com tooltip de zoom */}
              <div className={styles.produtoImagemWrapper}>
                <div className={styles.produtoImagem}>
                  {(() => {
                    const imgKey = `${item.catalogo?.nome}_${idx}`;
                    if (imgError[imgKey]) return <div className={styles.semImagem}>📷</div>;

                    const imgSrc = (prod.imagemUrl && prod.imagemUrl.startsWith('data:'))
                      ? prod.imagemUrl
                      : `/api/produtos/imagem/${prod.codigo}`;

                    return (
                      <img
                        src={imgSrc}
                        alt={prod.descricao}
                        className={styles.imgProduto}
                        onError={() => setImgError(prev => ({ ...prev, [imgKey]: true }))}
                      />
                    );
                  })()}
                </div>
                {/* Tooltip com imagem ampliada */}
                {!imgError[`${item.catalogo?.nome}_${idx}`] && (
                  <div className={styles.imgTooltip}>
                    <img
                      src={
                        (prod.imagemUrl && prod.imagemUrl.startsWith('data:'))
                          ? prod.imagemUrl
                          : `/api/produtos/imagem/${prod.codigo}`
                      }
                      alt={prod.descricao}
                    />
                    <span className={styles.imgTooltipLabel}>{prod.descricao}</span>
                  </div>
                )}
              </div>

              {/* Detalhes */}
              <div className={styles.produtoDetalhes}>
                <div className={styles.produtoNome}>{prod.descricao}</div>
                <div className={styles.produtoCodigo}>{prod.codigo}</div>
                <div className={styles.produtoFonte}>{formatarFonte(prod.fonte)}</div>

                {/* Métricas */}
                <div className={styles.metricas}>
                  <div className={`${styles.metrica} ${prod.estoque > 0 ? styles.metricaPositiva : styles.metricaZero}`}>
                    <span className={styles.metricaLabel}>Estoque</span>
                    <span className={styles.metricaValor}>{prod.estoque}</span>
                  </div>

                  <div className={styles.metrica}>
                    <span className={styles.metricaLabel}>Total vendido</span>
                    <span className={styles.metricaValor}>{prod.totalVendas}</span>
                  </div>

                  <div className={styles.metrica}>
                    <span className={styles.metricaLabel}>Média/mês</span>
                    <span className={styles.metricaValor}>
                      {prod.mediaMensal ? prod.mediaMensal.toFixed(1) : '0'}
                    </span>
                  </div>

                  {prod.preco > 0 && (
                    <div className={styles.metrica}>
                      <span className={styles.metricaLabel}>Preço</span>
                      <span className={styles.metricaValor}>{formatarMoeda(prod.preco)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Produto não encontrado */}
      {!item.encontrado && (
        <div className={styles.naoEncontradoInfo}>
          <p>Este produto do catálogo não foi identificado no estoque.</p>
          <span className={styles.dica}>
            💡 Considere cadastrar se tiver interesse
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Componente Principal ────────────────────────────────────────────────────

export default function CatalogoFornecedor() {
  const [arquivo, setArquivo] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [analisando, setAnalisando] = useState(false);
  const [progresso, setProgresso] = useState('');
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);
  const [filtro, setFiltro] = useState('todos'); // todos | encontrados | nao_encontrados
  const inputRef = useRef(null);

  // Drag & Drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragging(false), []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    const tiposAceitos = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (file && tiposAceitos.includes(file.type)) {
      setArquivo(file);
      setResultado(null);
      setErro(null);
    } else {
      setErro('Formato não suportado. Envie PDF, PNG ou JPEG.');
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArquivo(file);
      setResultado(null);
      setErro(null);
    }
  };

  const handleAnalisar = async () => {
    if (!arquivo) {
      setErro('Selecione um arquivo (PDF, PNG ou JPEG) antes de analisar.');
      return;
    }

    setAnalisando(true);
    setErro(null);
    setResultado(null);
    setProgresso('Enviando arquivo para o servidor...');

    try {
      const formData = new FormData();
      formData.append('catalogo', arquivo);

      setProgresso('Analisando conteúdo com inteligência artificial...');

      const token = getToken();

      if (!token) {
        throw new Error('Sessão expirada. Faça login novamente para continuar.');
      }

      const response = await fetch('/api/catalogo-fornecedor/analisar', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      setProgresso('Analisando produtos com inteligência artificial...');

      // Tratamento explícito de status HTTP
      if (response.status === 401) {
        throw new Error('Sessão expirada. Recarregue a página e faça login novamente.');
      }
      if (response.status === 413) {
        throw new Error('Arquivo muito grande. O tamanho máximo permitido é 50MB.');
      }
      if (response.status === 400) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Arquivo inválido. Envie um PDF válido.');
      }
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Erro ao processar (HTTP ${response.status})`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Erro desconhecido na análise');
      }

      setResultado(data.data);
      setProgresso('');
    } catch (err) {
      setErro(err.message);
      setProgresso('');
    } finally {
      setAnalisando(false);
    }
  };

  // Filtrar resultados
  const resultadosFiltrados = resultado?.resultados?.filter(r => {
    if (filtro === 'encontrados') return r.encontrado;
    if (filtro === 'nao_encontrados') return !r.encontrado;
    return true;
  }) || [];

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>🔍</div>
        <div>
          <h1 className={styles.titulo}>Análise de Catálogo de Fornecedor</h1>
          <p className={styles.subtitulo}>
            Envie o catálogo (PDF, PNG ou JPEG) e a IA identificará os produtos e cruzará com seu estoque
          </p>
        </div>
      </div>

      {/* Área de Upload */}
      {!resultado && (
        <div
          className={`${styles.dropzone} ${dragging ? styles.dropzoneAtivo : ''} ${arquivo ? styles.dropzoneComArquivo : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
            onChange={handleFileChange}
            className={styles.inputOculto}
          />

          {arquivo ? (
            <div className={styles.arquivoSelecionado}>
              <span className={styles.pdfIcone}>{arquivo.type?.startsWith('image/') ? '🖼️' : '📄'}</span>
              <div className={styles.arquivoInfo}>
                <span className={styles.arquivoNome}>{arquivo.name}</span>
                <span className={styles.arquivoTamanho}>
                  {(arquivo.size / 1024 / 1024).toFixed(1)} MB
                </span>
              </div>
              <button
                className={styles.btnTrocar}
                onClick={(e) => { e.stopPropagation(); setArquivo(null); }}
              >
                Trocar arquivo
              </button>
            </div>
          ) : (
            <div className={styles.dropzoneConteudo}>
              <div className={styles.dropzoneIcone}>📂</div>
              <p className={styles.dropzoneTexto}>
                <strong>Arraste o catálogo aqui</strong>
              </p>
              <p className={styles.dropzoneSubtexto}>ou clique para selecionar o arquivo</p>
              <span className={styles.dropzoneLimite}>PDF · PNG · JPEG · máx. 50MB</span>
            </div>
          )}
        </div>
      )}

      {/* Botão Analisar */}
      {arquivo && !resultado && (
        <div className={styles.acoes}>
          <button
            className={styles.btnAnalisar}
            onClick={handleAnalisar}
            disabled={analisando}
            id="btn-analisar-catalogo"
          >
            {analisando ? (
              <>
                <span className={styles.spinner}></span>
                Analisando...
              </>
            ) : (
              <>🤖 Analisar com IA</>
            )}
          </button>

          {analisando && progresso && (
            <div className={styles.progressoContainer}>
              <div className={styles.progressoBarra}>
                <div className={styles.progressoAnimado}></div>
              </div>
              <p className={styles.progressoTexto}>{progresso}</p>
            </div>
          )}
        </div>
      )}

      {/* Erro */}
      {erro && (
        <div className={styles.erroContainer}>
          <span>⚠️</span>
          <p>{erro}</p>
          <button onClick={() => setErro(null)} className={styles.btnFecharErro}>×</button>
        </div>
      )}

      {/* Resultados */}
      {resultado && (
        <div className={styles.resultados}>
          {/* Resumo */}
          <div className={styles.resumoContainer}>
            <div className={styles.resumoHeader}>
              <div>
                <h2 className={styles.resumoTitulo}>Análise Concluída</h2>
                <p className={styles.resumoArquivo}>📄 {resultado.nomeArquivo}</p>
              </div>
              <button
                className={styles.btnNovaAnalise}
                onClick={() => { setResultado(null); setArquivo(null); }}
              >
                Nova Análise
              </button>
            </div>

            <div className={styles.resumoCards}>
              <div className={styles.resumoCard}>
                <span className={styles.resumoNum}>{resultado.resumo.totalIdentificados}</span>
                <span className={styles.resumoLabel}>Produtos no catálogo</span>
              </div>
              <div className={`${styles.resumoCard} ${styles.resumoCardVerde}`}>
                <span className={styles.resumoNum}>{resultado.resumo.totalEncontrados}</span>
                <span className={styles.resumoLabel}>Encontrados no estoque</span>
              </div>
              <div className={`${styles.resumoCard} ${styles.resumoCardVermelho}`}>
                <span className={styles.resumoNum}>{resultado.resumo.totalNaoEncontrados}</span>
                <span className={styles.resumoLabel}>Não cadastrados</span>
              </div>
              <div className={`${styles.resumoCard} ${styles.resumoCardAzul}`}>
                <span className={styles.resumoNum}>{resultado.resumo.percentualCobertura}%</span>
                <span className={styles.resumoLabel}>Cobertura do catálogo</span>
              </div>
            </div>

            {/* Barra de progresso de cobertura */}
            <div className={styles.coberturaBar}>
              <div className={styles.coberturaBarFill} style={{ width: `${resultado.resumo.percentualCobertura}%` }} />
            </div>
          </div>

          {/* Filtros */}
          <div className={styles.filtros}>
            <button
              className={`${styles.filtroBtn} ${filtro === 'todos' ? styles.filtroAtivo : ''}`}
              onClick={() => setFiltro('todos')}
            >
              Todos ({resultado.resumo.totalIdentificados})
            </button>
            <button
              className={`${styles.filtroBtn} ${filtro === 'encontrados' ? styles.filtroAtivo : ''}`}
              onClick={() => setFiltro('encontrados')}
            >
              ✅ Encontrados ({resultado.resumo.totalEncontrados})
            </button>
            <button
              className={`${styles.filtroBtn} ${filtro === 'nao_encontrados' ? styles.filtroAtivo : ''}`}
              onClick={() => setFiltro('nao_encontrados')}
            >
              ❌ Não cadastrados ({resultado.resumo.totalNaoEncontrados})
            </button>
          </div>

          {/* Grid de cards */}
          <div className={styles.grid}>
            {resultadosFiltrados.map((item, idx) => (
              <CardResultado key={idx} item={item} />
            ))}
          </div>

          {resultadosFiltrados.length === 0 && (
            <div className={styles.semResultados}>
              <span>🔍</span>
              <p>Nenhum resultado para este filtro</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
